import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import {
  FiRefreshCw,
  FiXCircle,
  FiCopy,
  FiFlag,
  FiCheck,
  FiX,
  FiSave,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import { getDatabase, ref, get, set, push } from "firebase/database";
import PointWarning from "./components/PointWarning";
import {
  CLASSIFIED_AD_LENGTHS,
  CLASSIFIED_AD_STYLES,
  DEFAULT_CLASSIFIED_AD_LENGTH,
  DEFAULT_CLASSIFIED_AD_STYLES,
  DEFAULT_USE_EMOJI,
  DEFAULT_USE_CONTACT_INFO,
  DEFAULT_CONTACT_INFO,
} from "./constants/classifiedAdSettings";
import PostLanguageSelector from "./components/PostLanguageSelector";
import { getLanguageName } from "./constants/languages";
import { sendPostReportNotification } from "./utils/telegramNotification";

const ClassifiedAdGenerator = ({ isEmbedded = false }) => {
  // State management
  const [requirements, setRequirements] = useState("");
  const [postLength, setPostLength] = useState(DEFAULT_CLASSIFIED_AD_LENGTH);
  const [postStyles, setPostStyles] = useState(DEFAULT_CLASSIFIED_AD_STYLES);
  const [selectedLanguage, setSelectedLanguage] = useState("vi");
  const [useEmoji, setUseEmoji] = useState(DEFAULT_USE_EMOJI);
  const [useContactInfo, setUseContactInfo] = useState(
    DEFAULT_USE_CONTACT_INFO
  );
  const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT_INFO);
  const [generatedAd, setGeneratedAd] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [userPoints, setUserPoints] = useState(0);

  // Edit mode states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [hasEdited, setHasEdited] = useState(false);

  // Report states
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // History states
  const [showHistory, setShowHistory] = useState(false);
  const [adHistory, setAdHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryAd, setSelectedHistoryAd] = useState(null);
  const [isSavingAd, setIsSavingAd] = useState(false);

  const navigate = useNavigate();
  const generatedAdRef = useRef(null);

  // Authentication check and fetch user points
  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    const fetchUserPoints = async () => {
      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const pointsRef = ref(db, `users/${userId}/points`);
        const snapshot = await get(pointsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.points !== undefined && data.purchasedPoints !== undefined) {
            setUserPoints(data.points + data.purchasedPoints);
          } else if (typeof data.points === "number") {
            setUserPoints(data.points);
          } else {
            setUserPoints(0);
          }
        } else {
          setUserPoints(0);
        }
      } catch (error) {
        console.error("Error fetching user points:", error);
        setUserPoints(0);
      }
    };

    fetchUserPoints();
    loadUserSettings();
  }, [navigate]);

  // Load user settings from localStorage and Firebase
  const loadUserSettings = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(
        db,
        `users/${userId}/settings/classifiedAdGenerator`
      );
      const snapshot = await get(settingsRef);

      if (snapshot.exists()) {
        const settings = snapshot.val();
        setPostLength(settings.postLength || DEFAULT_CLASSIFIED_AD_LENGTH);
        setPostStyles(settings.postStyles || DEFAULT_CLASSIFIED_AD_STYLES);
        setSelectedLanguage(settings.selectedLanguage || "vi");
        setUseEmoji(
          settings.useEmoji !== undefined
            ? settings.useEmoji
            : DEFAULT_USE_EMOJI
        );
        setUseContactInfo(
          settings.useContactInfo !== undefined
            ? settings.useContactInfo
            : DEFAULT_USE_CONTACT_INFO
        );
        setContactInfo(settings.contactInfo || DEFAULT_CONTACT_INFO);
      }

      // Load from localStorage if not in Firebase
      const savedLanguage = localStorage.getItem("selectedLanguage");
      if (savedLanguage && !snapshot.exists()) {
        setSelectedLanguage(savedLanguage);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  // Save user settings
  const saveUserSettings = async (newSettings) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      // Save to localStorage
      const currentSettings = JSON.parse(
        localStorage.getItem("classifiedAdSettings") || "{}"
      );
      const updatedSettings = { ...currentSettings, ...newSettings };
      localStorage.setItem(
        "classifiedAdSettings",
        JSON.stringify(updatedSettings)
      );

      // Save to Firebase
      const db = getDatabase();
      const settingsRef = ref(
        db,
        `users/${userId}/settings/classifiedAdGenerator`
      );
      const snapshot = await get(settingsRef);
      const existingSettings = snapshot.exists() ? snapshot.val() : {};
      await set(settingsRef, { ...existingSettings, ...newSettings });
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  // Check user points
  const checkPoints = () => {
    if (userPoints <= 0) {
      toast.error(
        "Bạn đã hết điểm! Vui lòng nạp thêm điểm để tiếp tục sử dụng dịch vụ.",
        { position: "top-right", autoClose: 5000 }
      );
      return false;
    }
    return true;
  };

  // Handle post style change
  const handlePostStyleChange = (style) => {
    const newStyles = postStyles.includes(style)
      ? postStyles.filter((s) => s !== style)
      : [...postStyles, style];
    setPostStyles(newStyles);
    saveUserSettings({ postStyles: newStyles });
  };

  // Handle language change
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    localStorage.setItem("selectedLanguage", language);
    saveUserSettings({ selectedLanguage: language });
  };

  // Handle post length change
  const handlePostLengthChange = (length) => {
    setPostLength(length);
    saveUserSettings({ postLength: length });
  };

  // Handle emoji toggle
  const handleEmojiToggle = () => {
    const newValue = !useEmoji;
    setUseEmoji(newValue);
    saveUserSettings({ useEmoji: newValue });
  };

  // Handle contact info toggle
  const handleContactInfoToggle = () => {
    const newValue = !useContactInfo;
    setUseContactInfo(newValue);
    saveUserSettings({ useContactInfo: newValue });
  };

  // Handle contact info change
  const handleContactInfoChange = (value) => {
    setContactInfo(value);
    saveUserSettings({ contactInfo: value });
  };

  // Clear requirements
  const clearRequirements = () => {
    setRequirements("");
  };

  // Generate classified ad
  const generateAd = async () => {
    // Check if user has edited and wants to regenerate
    if (generatedAd && hasEdited) {
      if (
        !window.confirm(
          "Bạn đã chỉnh sửa bài đăng. Tạo bài đăng mới sẽ xóa các chỉnh sửa của bạn. Bạn có chắc chắn muốn tiếp tục?"
        )
      ) {
        return;
      }
    }

    if (!checkPoints()) return;

    if (!requirements.trim()) {
      toast.error("Vui lòng nhập yêu cầu bài đăng tin!", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    setIsGenerating(true);
    setHasEdited(false);
    const userId = localStorage.getItem("userId");

    const options = {
      requirements: requirements,
      postLength: postLength,
      postStyles: postStyles.join(", "),
      language: selectedLanguage,
      useEmoji: useEmoji,
      contactInfo:
        useContactInfo && contactInfo.trim() ? contactInfo.trim() : null,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/content/generate-classified-ad`,
        { options, userId }
      );

      if (response.data.success) {
        const { title, content } = response.data.data;
        const newAd = {
          title,
          content,
          originalRequirements: requirements,
          createdAt: Date.now(),
          settings: { postLength, postStyles, language: selectedLanguage },
        };

        setGeneratedAd(newAd);
        setEditedTitle(title);
        setEditedContent(content);

        // Save to history
        await saveToHistory(newAd);

        // Scroll to result
        setTimeout(() => {
          if (generatedAdRef.current) {
            generatedAdRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } else {
        toast.error(
          response.data.message ||
            "Không thể tạo bài đăng. Vui lòng thử lại sau.",
          { position: "top-right", autoClose: 5000 }
        );
      }
    } catch (error) {
      console.error("Lỗi khi tạo bài đăng:", error);
      toast.error("Đã xảy ra lỗi khi tạo bài đăng. Vui lòng thử lại sau.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerate ad with same parameters
  const regenerateAd = async () => {
    if (hasEdited) {
      if (
        !window.confirm(
          "Bạn đã chỉnh sửa bài đăng. Tạo lại sẽ xóa các chỉnh sửa của bạn. Bạn có chắc chắn muốn tiếp tục?"
        )
      ) {
        return;
      }
    }
    await generateAd();
  };

  // Save to history
  const saveToHistory = async (ad) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const historyRef = ref(db, `users/${userId}/classifiedAdHistory`);
      await push(historyRef, ad);
    } catch (error) {
      console.error("Error saving to history:", error);
    }
  };

  // Load history
  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const historyRef = ref(db, `users/${userId}/classifiedAdHistory`);
      const snapshot = await get(historyRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const historyArray = Object.entries(data)
          .map(([key, value]) => ({ id: key, ...value }))
          .sort((a, b) => b.createdAt - a.createdAt)
          .slice(0, 20); // Max 20 items
        setAdHistory(historyArray);
        // Auto-select first item
        if (historyArray.length > 0) {
          setSelectedHistoryAd(historyArray[0]);
        }
      } else {
        setAdHistory([]);
        setSelectedHistoryAd(null);
      }
    } catch (error) {
      console.error("Error loading history:", error);
      setAdHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Select from history (preview in right panel)
  const selectHistoryItem = (ad) => {
    setSelectedHistoryAd(ad);
  };

  // Use selected history ad
  const useFromHistory = (ad) => {
    setGeneratedAd(ad);
    setEditedTitle(ad.title);
    setEditedContent(ad.content);
    setHasEdited(false);
    setShowHistory(false);
    setSelectedHistoryAd(null);
  };

  // Save current ad to history manually
  const saveCurrentAd = async () => {
    if (!generatedAd) return;

    setIsSavingAd(true);
    try {
      const adToSave = {
        title: hasEdited ? editedTitle : generatedAd.title,
        content: hasEdited ? editedContent : generatedAd.content,
        originalRequirements: generatedAd.originalRequirements || requirements,
        createdAt: Date.now(),
        settings: generatedAd.settings || {
          postLength,
          postStyles,
          language: selectedLanguage,
        },
      };

      await saveToHistory(adToSave);
      toast.success("Đã lưu bài đăng vào lịch sử!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving ad:", error);
      toast.error("Không thể lưu bài đăng. Vui lòng thử lại.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSavingAd(false);
    }
  };

  // Copy functions
  const copyTitle = () => {
    if (!generatedAd) return;
    const titleToCopy = hasEdited ? editedTitle : generatedAd.title;
    navigator.clipboard
      .writeText(titleToCopy)
      .then(() => {
        toast.success("Đã sao chép tiêu đề vào clipboard!", {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .catch(() => {
        toast.error("Không thể sao chép. Vui lòng thử lại.", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  const copyContent = () => {
    if (!generatedAd) return;
    const contentToCopy = hasEdited ? editedContent : generatedAd.content;
    navigator.clipboard
      .writeText(contentToCopy)
      .then(() => {
        toast.success("Đã sao chép nội dung vào clipboard!", {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .catch(() => {
        toast.error("Không thể sao chép. Vui lòng thử lại.", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  const copyAll = () => {
    if (!generatedAd) return;
    const titleToCopy = hasEdited ? editedTitle : generatedAd.title;
    const contentToCopy = hasEdited ? editedContent : generatedAd.content;
    const fullText = `${titleToCopy}\n\n${contentToCopy}`;
    navigator.clipboard
      .writeText(fullText)
      .then(() => {
        toast.success("Đã sao chép tất cả vào clipboard!", {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .catch(() => {
        toast.error("Không thể sao chép. Vui lòng thử lại.", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  // Edit functions
  const startEditTitle = () => {
    setEditedTitle(generatedAd.title);
    setIsEditingTitle(true);
  };

  const saveEditTitle = () => {
    if (editedTitle !== generatedAd.title) {
      setGeneratedAd((prev) => ({ ...prev, title: editedTitle }));
      setHasEdited(true);
      toast.success("Đã lưu thay đổi tiêu đề!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsEditingTitle(false);
  };

  const cancelEditTitle = () => {
    setEditedTitle(generatedAd.title);
    setIsEditingTitle(false);
  };

  const startEditContent = () => {
    setEditedContent(generatedAd.content);
    setIsEditingContent(true);
  };

  const saveEditContent = () => {
    if (editedContent !== generatedAd.content) {
      setGeneratedAd((prev) => ({ ...prev, content: editedContent }));
      setHasEdited(true);
      toast.success("Đã lưu thay đổi nội dung!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsEditingContent(false);
  };

  const cancelEditContent = () => {
    setEditedContent(generatedAd.content);
    setIsEditingContent(false);
  };

  // Report functions
  const openReportModal = () => {
    setShowReportModal(true);
    setReportType("");
    setReportDescription("");
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setReportType("");
    setReportDescription("");
  };

  const handleReportSubmit = async () => {
    if (!reportType) {
      toast.error("Vui lòng chọn loại lỗi!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmittingReport(true);
    const userId = localStorage.getItem("userId");

    try {
      const reportData = {
        userId: userId || "Người dùng ẩn danh",
        errorType: reportType,
        description: reportDescription || "Không có mô tả chi tiết",
        language: getLanguageName(selectedLanguage),
        postLength: postLength,
        postStyles: postStyles.join(", "),
        postTitle: generatedAd?.title || "Không có tiêu đề",
        postContent: generatedAd?.content || "Không có nội dung",
        requirements: requirements,
        userAgent: navigator.userAgent,
        postType: "classifiedAdGenerator",
      };

      const success = await sendPostReportNotification(reportData);

      if (success) {
        toast.success(
          "🙏 Cảm ơn bạn đã báo cáo! Chúng tôi sẽ cải thiện hệ thống.",
          { position: "top-right", autoClose: 4000 }
        );
        closeReportModal();
      } else {
        toast.error("Không thể gửi báo cáo. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Lỗi khi gửi báo cáo:", error);
      toast.error("Đã xảy ra lỗi khi gửi báo cáo. Vui lòng thử lại sau.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Report types for classified ads
  const reportTypes = [
    {
      value: "content_not_suitable",
      label: "📝 Nội dung không phù hợp với tin rao vặt",
    },
    { value: "title_not_attractive", label: "🏷️ Tiêu đề không hấp dẫn" },
    { value: "missing_info", label: "❓ Thiếu thông tin quan trọng" },
    { value: "language_error", label: "🌐 Ngôn ngữ sai hoặc lỗi ngữ pháp" },
    { value: "format_error", label: "📋 Định dạng bài đăng bị lỗi" },
    { value: "other", label: "❓ Lỗi khác" },
  ];

  return (
    <div className={`min-h-screen bg-gray-50 font-sans ${isEmbedded ? 'min-h-0 bg-transparent' : ''}`}>
      <ToastContainer />
      <div className={`max-w-full mx-auto px-2 sm:px-3 ${isEmbedded ? 'pt-0 pb-0' : 'pt-6 pb-8'}`}>
        {!isEmbedded && (
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 text-center">
              TẠO BÀI ĐĂNG TIN RAO VẶT VỚI AI
            </h1>
          </div>
        )}

        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoints}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Left side - Input form */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-md shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Thông tin bài đăng
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowHistory(true);
                      loadHistory();
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300"
                  >
                    📜 Lịch sử
                  </button>
                  <a
                    href="https://youtu.be/DLYXvddKYas?si=CcT_l5BkG9OvB7tW"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 text-sm font-medium rounded-md shadow-sm transition duration-300"
                    title="Xem video hướng dẫn trên Youtube"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                    <span>Hướng dẫn</span>
                  </a>
                  <a
                    href="https://zalo.me/0394932696"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 text-sm font-medium rounded-md shadow-sm transition duration-300"
                    title="Liên hệ hỗ trợ qua Zalo"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.833.94z" />
                    </svg>
                    <span>Hỗ trợ Zalo</span>
                  </a>
                </div>
              </div>

              {/* Requirements input */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Nhập yêu cầu bài đăng tin
                </label>
                <div className="relative">
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700"
                    rows="5"
                    maxLength={2000}
                    value={requirements}
                    onChange={(e) => setRequirements(e.target.value)}
                    placeholder="Ví dụ: Bán căn hộ 2 phòng ngủ, 70m2, tầng 10, view sông, full nội thất, giá 2.5 tỷ, quận 7, TP.HCM. Liên hệ: 0901234567"
                  ></textarea>
                  {requirements && (
                    <button
                      onClick={clearRequirements}
                      className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                      title="Xóa nội dung"
                    >
                      <FiXCircle size={20} />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {requirements.length}/2000 ký tự
                </p>
              </div>

              {/* Post length selector */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Độ dài bài đăng
                </label>
                <div className="flex w-full rounded-md shadow-sm">
                  {CLASSIFIED_AD_LENGTHS.map(({ value, label }, index) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 py-2 px-2 text-center text-sm font-medium
                        ${index === 0 ? "rounded-l-md" : ""}
                        ${
                          index === CLASSIFIED_AD_LENGTHS.length - 1
                            ? "rounded-r-md"
                            : ""
                        }
                        ${
                          postLength === value
                            ? "bg-blue-600 text-white z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => handlePostLengthChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Post styles selector */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Phong cách bài đăng
                </label>
                <div className="flex flex-wrap gap-2">
                  {CLASSIFIED_AD_STYLES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`py-2 px-4 rounded-full text-sm font-medium
                        ${
                          postStyles.includes(value)
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => handlePostStyleChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language selector */}
              <PostLanguageSelector
                selectedLanguage={selectedLanguage}
                onLanguageChange={handleLanguageChange}
                disabled={isGenerating}
              />

              {/* Emoji toggle */}
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <label className="block text-gray-700 font-medium">
                    Sử dụng icon/emoji trong bài đăng
                  </label>
                  <button
                    type="button"
                    onClick={handleEmojiToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      useEmoji ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        useEmoji ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {useEmoji
                    ? "✨ Bài đăng sẽ có icon/emoji để thu hút hơn"
                    : "Bài đăng sẽ không có icon/emoji"}
                </p>
              </div>

              {/* Contact info section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-gray-700 font-medium">
                    Thông tin liên hệ
                  </label>
                  <button
                    type="button"
                    onClick={handleContactInfoToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      useContactInfo ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        useContactInfo ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  {useContactInfo
                    ? "📞 Thông tin liên hệ sẽ được thêm vào cuối bài đăng"
                    : "Không thêm thông tin liên hệ vào bài đăng"}
                </p>
                {useContactInfo && (
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700"
                    rows="3"
                    maxLength={500}
                    value={contactInfo}
                    onChange={(e) => handleContactInfoChange(e.target.value)}
                    placeholder="Ví dụ: Liên hệ: 0901234567 (Zalo/Call)&#10;Địa chỉ: 123 Nguyễn Văn A, Q.1, TP.HCM&#10;Email: example@email.com"
                  ></textarea>
                )}
                {useContactInfo && (
                  <p className="text-xs text-gray-400 mt-1">
                    💡 Thông tin này sẽ được lưu lại cho các lần đăng bài sau
                  </p>
                )}
              </div>

              {/* Generate button */}
              <div className="flex flex-col space-y-4">
                <button
                  className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md flex items-center justify-center transition-colors disabled:opacity-60 disabled:pointer-events-none"
                  onClick={generateAd}
                  disabled={isGenerating || userPoints <= 0}
                >
                  {isGenerating ? (
                    <div className="flex items-center">
                      <ClipLoader size={20} color="#ffffff" className="mr-2" />
                      <span>Đang tạo bài đăng...</span>
                    </div>
                  ) : (
                    <>
                      <FiRefreshCw className="mr-2" />
                      Tạo bài đăng
                    </>
                  )}
                </button>
                {userPoints <= 0 && (
                  <p className="text-red-500 text-sm mt-2 text-center">
                    Bạn không đủ điểm để tạo bài đăng. Vui lòng nạp thêm điểm!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right side - Result */}
          <div className="w-full md:w-1/2">
            {generatedAd ? (
              <div
                ref={generatedAdRef}
                className="bg-white rounded-md shadow-md p-5 overflow-hidden mb-20 md:mb-0"
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Bài đăng đã tạo
                    {hasEdited && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Đã chỉnh sửa
                      </span>
                    )}
                  </h2>
                  <button
                    onClick={openReportModal}
                    className="px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-md font-medium text-sm flex items-center"
                    title="Báo cáo lỗi bài đăng"
                  >
                    <FiFlag className="mr-1" size={16} />
                    Báo cáo
                  </button>
                </div>

                {/* Title section */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">
                      Tiêu đề
                    </label>
                    {!isEditingTitle ? (
                      <div className="flex gap-2">
                        <button
                          onClick={startEditTitle}
                          className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-sm flex items-center"
                        >
                          ✏️ Sửa
                        </button>
                        <button
                          onClick={copyTitle}
                          className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-sm flex items-center"
                        >
                          <FiCopy className="mr-1" size={14} />
                          Copy
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={saveEditTitle}
                          className="px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-sm flex items-center"
                        >
                          <FiCheck className="mr-1" size={14} />
                          Lưu
                        </button>
                        <button
                          onClick={cancelEditTitle}
                          className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm flex items-center"
                        >
                          <FiX className="mr-1" size={14} />
                          Hủy
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditingTitle ? (
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-bold text-lg text-gray-800"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                    />
                  ) : (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-md border border-blue-100">
                      <p className="font-bold text-lg text-gray-800">
                        {hasEdited ? editedTitle : generatedAd.title}
                      </p>
                    </div>
                  )}
                </div>

                {/* Content section */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">
                      Nội dung
                    </label>
                    {!isEditingContent ? (
                      <div className="flex gap-2">
                        <button
                          onClick={startEditContent}
                          className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-sm flex items-center"
                        >
                          ✏️ Sửa
                        </button>
                        <button
                          onClick={copyContent}
                          className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-sm flex items-center"
                        >
                          <FiCopy className="mr-1" size={14} />
                          Copy
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={saveEditContent}
                          className="px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-sm flex items-center"
                        >
                          <FiCheck className="mr-1" size={14} />
                          Lưu
                        </button>
                        <button
                          onClick={cancelEditContent}
                          className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm flex items-center"
                        >
                          <FiX className="mr-1" size={14} />
                          Hủy
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditingContent ? (
                    <textarea
                      className="w-full px-4 py-3 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                      rows="10"
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                    />
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {hasEdited ? editedContent : generatedAd.content}
                      </p>
                    </div>
                  )}
                </div>

                {/* Save, Regenerate and Copy buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={saveCurrentAd}
                    disabled={isSavingAd}
                    className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md flex items-center justify-center transition-colors disabled:opacity-50"
                  >
                    {isSavingAd ? (
                      <div className="flex items-center">
                        <ClipLoader
                          size={16}
                          color="#ffffff"
                          className="mr-2"
                        />
                        Đang lưu...
                      </div>
                    ) : (
                      <>
                        <FiSave className="mr-2" size={18} />
                        Lưu bài đăng
                      </>
                    )}
                  </button>
                  <button
                    onClick={regenerateAd}
                    disabled={isGenerating}
                    className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md flex items-center justify-center transition-colors disabled:opacity-50"
                  >
                    <FiRefreshCw
                      className={`mr-2 ${isGenerating ? "animate-spin" : ""}`}
                      size={18}
                    />
                    Tạo lại
                  </button>
                  <button
                    onClick={copyAll}
                    className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md flex items-center justify-center transition-colors"
                  >
                    <FiCopy className="mr-2" size={18} />
                    Sao chép tất cả
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-md shadow-md p-5 flex items-center justify-center min-h-[400px]">
                <div className="text-center text-gray-500">
                  <p className="text-lg mb-2">Chưa có bài đăng nào được tạo</p>
                  <p className="text-sm">
                    Nhập yêu cầu và nhấn "Tạo bài đăng" để bắt đầu
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Báo cáo lỗi bài đăng
              </h3>
              <button
                onClick={closeReportModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">
                Loại lỗi
              </label>
              <div className="space-y-2">
                {reportTypes.map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                      reportType === value
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportType"
                      value={value}
                      checked={reportType === value}
                      onChange={(e) => setReportType(e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">
                Mô tả chi tiết (tùy chọn)
              </label>
              <textarea
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Mô tả thêm về lỗi bạn gặp phải..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeReportModal}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleReportSubmit}
                disabled={isSubmittingReport || !reportType}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:opacity-50"
              >
                {isSubmittingReport ? (
                  <div className="flex items-center justify-center">
                    <ClipLoader size={16} color="#ffffff" className="mr-2" />
                    Đang gửi...
                  </div>
                ) : (
                  "Gửi báo cáo"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal - Two Column Layout (responsive) */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full p-4 sm:p-6 h-[95vh] md:h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Lịch sử bài đăng
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  ⏰ Bài viết được lưu trữ tối đa 7 ngày
                </p>
              </div>
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedHistoryAd(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="flex-1 flex flex-col md:flex-row gap-4 overflow-hidden min-h-0">
              {/* Left side - List */}
              <div
                className={`${
                  selectedHistoryAd ? "hidden md:flex" : "flex"
                } md:w-2/5 flex-col overflow-hidden md:border-r border-gray-200 md:pr-4`}
              >
                <p className="text-sm text-gray-500 mb-3">
                  Danh sách ({adHistory.length})
                </p>
                <div className="flex-1 overflow-y-auto">
                  {isLoadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <ClipLoader size={30} color="#3B82F6" />
                    </div>
                  ) : adHistory.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>Chưa có bài đăng nào trong lịch sử</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {adHistory.map((ad) => (
                        <div
                          key={ad.id}
                          onClick={() => selectHistoryItem(ad)}
                          className={`p-3 border rounded-md cursor-pointer transition-colors ${
                            selectedHistoryAd?.id === ad.id
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                          }`}
                        >
                          <p className="font-medium text-gray-800 text-sm line-clamp-1">
                            {ad.title}
                          </p>
                          <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                            {ad.content}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(ad.createdAt).toLocaleString("vi-VN")}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Detail */}
              <div
                className={`${
                  selectedHistoryAd ? "flex" : "hidden md:flex"
                } md:w-3/5 flex-col overflow-hidden md:pl-2 flex-1`}
              >
                {/* Mobile back button */}
                {selectedHistoryAd && (
                  <button
                    onClick={() => setSelectedHistoryAd(null)}
                    className="md:hidden flex items-center text-blue-600 mb-3 text-sm"
                  >
                    ← Quay lại danh sách
                  </button>
                )}
                <p className="text-sm text-gray-500 mb-3">Chi tiết bài đăng</p>
                {selectedHistoryAd ? (
                  <div className="flex-1 overflow-y-auto">
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700 font-medium text-sm">
                          Tiêu đề
                        </label>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              selectedHistoryAd.title
                            );
                            toast.success("Đã sao chép tiêu đề!", {
                              position: "top-right",
                              autoClose: 2000,
                            });
                          }}
                          className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-xs sm:text-sm flex items-center"
                        >
                          <FiCopy className="mr-1" size={12} />
                          Copy
                        </button>
                      </div>
                      <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-md border border-blue-100">
                        <p className="font-bold text-gray-800 text-sm sm:text-base">
                          {selectedHistoryAd.title}
                        </p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700 font-medium text-sm">
                          Nội dung
                        </label>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              selectedHistoryAd.content
                            );
                            toast.success("Đã sao chép nội dung!", {
                              position: "top-right",
                              autoClose: 2000,
                            });
                          }}
                          className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-xs sm:text-sm flex items-center"
                        >
                          <FiCopy className="mr-1" size={12} />
                          Copy
                        </button>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200 max-h-[200px] sm:max-h-[300px] overflow-y-auto">
                        <p className="text-gray-700 whitespace-pre-wrap text-xs sm:text-sm">
                          {selectedHistoryAd.content}
                        </p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-gray-700 font-medium mb-2 text-sm">
                        Thông tin
                      </label>
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200 text-xs sm:text-sm text-gray-600">
                        <p>
                          📅 Ngày tạo:{" "}
                          {new Date(selectedHistoryAd.createdAt).toLocaleString(
                            "vi-VN"
                          )}
                        </p>
                        {selectedHistoryAd.settings && (
                          <>
                            <p>
                              📏 Độ dài: {selectedHistoryAd.settings.postLength}
                            </p>
                            <p>
                              🎨 Phong cách:{" "}
                              {selectedHistoryAd.settings.postStyles?.join(
                                ", "
                              ) || "N/A"}
                            </p>
                            <p>
                              🌐 Ngôn ngữ:{" "}
                              {getLanguageName(
                                selectedHistoryAd.settings.language
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <p className="text-lg mb-1">👈</p>
                      <p>Chọn một bài đăng từ danh sách để xem chi tiết</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedHistoryAd(null);
                }}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors text-sm sm:text-base"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassifiedAdGenerator;
